import { useEffect, useState } from 'react'
import { api } from '../api'
import { Card, Button, Form, Input, InputNumber, Select, DatePicker, Tag, Empty, Spin, Space, Typography, Row, Col, Drawer, Statistic, message, Popconfirm } from 'antd'
import { PlusOutlined, DeleteOutlined, ClockCircleOutlined, CalendarOutlined, EuroOutlined, UserOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import useIsMobile from '../hooks/useIsMobile'

const { Title, Text } = Typography

export default function Jornales() {
  const isMobile = useIsMobile()
  const [jornales, setJornales] = useState([])
  const [cultivos, setCultivos] = useState([])
  const [cargando, setCargando] = useState(true)
  const [mostrarForm, setMostrarForm] = useState(false)
  const [guardando, setGuardando] = useState(false)
  const [filtroMes, setFiltroMes] = useState(dayjs().format('YYYY-MM'))
  const [form] = Form.useForm()

  useEffect(() => { cargar() }, [])

  const cargar = async () => {
    try {
      const [j, c] = await Promise.all([api('/jornales'), api('/cultivos')])
      setJornales(j)
      setCultivos(c)
    } catch (err) {
      console.error('Error cargando jornales:', err)
    }
    setCargando(false)
  }

  const guardar = async (values) => {
    setGuardando(true)
    try {
      await api('/jornales', {
        method: 'POST',
        body: JSON.stringify({
          fecha: values.fecha.format('YYYY-MM-DD'),
          horas: values.horas,
          precio_hora: values.precio_hora || 0,
          trabajador: values.trabajador || '',
          descripcion: values.descripcion || '',
          cultivo_id: values.cultivo_id || null,
        }),
      })
      message.success('Jornal registrado')
      setMostrarForm(false)
      form.resetFields()
      cargar()
    } catch (err) {
      message.error('Error registrando jornal')
    }
    setGuardando(false)
  }

  const eliminar = async (id) => {
    try {
      await api(`/jornales/${id}`, { method: 'DELETE' })
      message.success('Jornal eliminado')
      cargar()
    } catch (err) {
      message.error('Error eliminando jornal')
    }
  }

  // Filtrar por mes
  const jornalesFiltrados = jornales.filter(j => j.fecha && j.fecha.startsWith(filtroMes))

  // Stats
  const totalHoras = jornalesFiltrados.reduce((s, j) => s + (parseFloat(j.horas) || 0), 0)
  const totalCoste = jornalesFiltrados.reduce((s, j) => s + ((parseFloat(j.horas) || 0) * (parseFloat(j.precio_hora) || 0)), 0)

  // Meses disponibles
  const mesesDisponibles = [...new Set(jornales.map(j => j.fecha?.substring(0, 7)).filter(Boolean))].sort().reverse()
  if (!mesesDisponibles.includes(filtroMes)) mesesDisponibles.unshift(filtroMes)

  const mesesNombre = { '01': 'Ene', '02': 'Feb', '03': 'Mar', '04': 'Abr', '05': 'May', '06': 'Jun', '07': 'Jul', '08': 'Ago', '09': 'Sep', '10': 'Oct', '11': 'Nov', '12': 'Dic' }

  return (
    <div style={{ minHeight: '100vh', background: '#f0f2f5' }}>
      <div style={{ background: 'linear-gradient(135deg, #1B3A6B 0%, #2E5FA3 100%)', padding: isMobile ? '14px 14px' : '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px' }}>
        <Title level={4} style={{ color: 'white', margin: 0, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>Jornales</Title>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => { form.resetFields(); form.setFieldsValue({ fecha: dayjs() }); setMostrarForm(true) }} style={{ background: '#2E7D32', flexShrink: 0 }}>
          {!isMobile && 'Nuevo'}
        </Button>
      </div>

      <Drawer
        title="Nuevo jornal"
        open={mostrarForm}
        onClose={() => setMostrarForm(false)}
        width={isMobile ? '100%' : 380}
        footer={
          <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
            <Button onClick={() => setMostrarForm(false)}>Cancelar</Button>
            <Button type="primary" loading={guardando} onClick={() => form.submit()}>Registrar</Button>
          </Space>
        }
      >
        <Form form={form} layout="vertical" onFinish={guardar}>
          <Form.Item label="Fecha" name="fecha" rules={[{ required: true }]}>
            <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
          </Form.Item>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item label="Horas" name="horas" rules={[{ required: true, message: 'Obligatorio' }]}>
                <InputNumber min={0.5} step={0.5} style={{ width: '100%' }} placeholder="Ej: 4" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="€/hora" name="precio_hora">
                <InputNumber min={0} step={0.5} style={{ width: '100%' }} placeholder="Ej: 12" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item label="Trabajador" name="trabajador">
            <Input prefix={<UserOutlined />} placeholder="Nombre del trabajador..." />
          </Form.Item>
          <Form.Item label="Cultivo" name="cultivo_id">
            <Select allowClear placeholder="Vincular a un cultivo (opcional)">
              {cultivos.filter(c => c.estado === 'activo').map(c => (
                <Select.Option key={c.id} value={c.id}>{c.nombre}</Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item label="Descripción" name="descripcion">
            <Input placeholder="Qué trabajo se realizó..." />
          </Form.Item>
        </Form>
      </Drawer>

      <div style={{ padding: '16px' }}>
        {/* Stats */}
        <Row gutter={8} style={{ marginBottom: '12px' }}>
          <Col span={12}>
            <Card size="small" style={{ borderRadius: '10px', textAlign: 'center' }}>
              <Statistic title={<Text style={{ fontSize: '11px' }}>Horas mes</Text>} value={totalHoras} precision={1} suffix="h" valueStyle={{ fontSize: '22px', color: '#6A1B9A' }} prefix={<ClockCircleOutlined />} />
            </Card>
          </Col>
          <Col span={12}>
            <Card size="small" style={{ borderRadius: '10px', textAlign: 'center' }}>
              <Statistic title={<Text style={{ fontSize: '11px' }}>Coste mes</Text>} value={totalCoste} precision={2} suffix="€" valueStyle={{ fontSize: '22px', color: '#E65100' }} prefix={<EuroOutlined />} />
            </Card>
          </Col>
        </Row>

        {/* Filtro mes */}
        <div style={{ marginBottom: '12px', display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
          {mesesDisponibles.map(m => {
            const [y, mo] = m.split('-')
            return (
              <Tag
                key={m}
                color={filtroMes === m ? '#1B3A6B' : undefined}
                onClick={() => setFiltroMes(m)}
                style={{ cursor: 'pointer', fontSize: '12px' }}
              >
                {mesesNombre[mo]} {y}
              </Tag>
            )
          })}
        </div>

        {cargando ? (
          <div style={{ textAlign: 'center', marginTop: '60px' }}><Spin size="large" /></div>
        ) : jornalesFiltrados.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <Space direction="vertical" size={4}>
                <Text type="secondary">No hay jornales este mes</Text>
                <Text type="secondary" style={{ fontSize: '12px' }}>Pulsa "Nuevo" o se registran automáticamente desde los cultivos</Text>
              </Space>
            }
            style={{ marginTop: '40px' }}
          />
        ) : (
          <Space direction="vertical" size={8} style={{ width: '100%' }}>
            {jornalesFiltrados.map(j => {
              const coste = (parseFloat(j.horas) || 0) * (parseFloat(j.precio_hora) || 0)
              const cultivo = cultivos.find(c => c.id === j.cultivo_id)
              return (
                <Card key={j.id} size="small" hoverable style={{ borderRadius: '12px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                        <Text style={{ fontSize: '12px' }}><CalendarOutlined /> {dayjs(j.fecha).format('DD/MM/YYYY')}</Text>
                        <Tag color="purple" style={{ fontSize: '11px' }}><ClockCircleOutlined /> {j.horas}h</Tag>
                        {j.precio_hora > 0 && <Tag color="orange" style={{ fontSize: '11px' }}>{j.horas}h x {j.precio_hora}€ = {coste.toFixed(2)}€</Tag>}
                      </div>
                      {j.trabajador && <Text style={{ fontSize: '13px', display: 'block', marginTop: '2px' }}><UserOutlined /> {j.trabajador}</Text>}
                      {j.descripcion && <Text style={{ fontSize: '12px', display: 'block', marginTop: '2px' }}>{j.descripcion}</Text>}
                      {cultivo && <Tag style={{ fontSize: '10px', marginTop: '4px' }}>{cultivo.nombre}</Tag>}
                    </div>
                    <Popconfirm title="¿Eliminar?" onConfirm={() => eliminar(j.id)} okText="Sí" cancelText="No">
                      <Button type="text" size="small" danger icon={<DeleteOutlined />} />
                    </Popconfirm>
                  </div>
                </Card>
              )
            })}
          </Space>
        )}
      </div>
    </div>
  )
}
